import { FileCode2, Heart, MoreHorizontal } from "lucide-react";
import { Fragment } from "react";
import { Link } from "react-router-dom";
import { formatShortTime } from "../../lib/format";
import type { SvgJob } from "../../lib/types";
import { JobProgress } from "./JobProgress";
import { JobStatusBadge } from "./JobStatusBadge";
import { ENGINE_OPTIONS, isActive } from "./status";

const DAY = 86_400_000;
type JobGroup = { label: string; jobs: SvgJob[] };

function groupJobs(jobs: SvgJob[]): JobGroup[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = today.getTime();
  const buckets: JobGroup[] = [
    { label: "今天", jobs: [] },
    { label: "近 3 天", jobs: [] },
    { label: "近 7 天", jobs: [] },
    { label: "近 30 天", jobs: [] },
    { label: "更早", jobs: [] },
  ];
  for (const job of jobs) {
    const createdAt = Date.parse(job.created_at);
    if (createdAt >= start) buckets[0]!.jobs.push(job);
    else if (createdAt >= start - 2 * DAY) buckets[1]!.jobs.push(job);
    else if (createdAt >= start - 6 * DAY) buckets[2]!.jobs.push(job);
    else if (createdAt >= start - 29 * DAY) buckets[3]!.jobs.push(job);
    else buckets[4]!.jobs.push(job);
  }
  return buckets.filter((group) => group.jobs.length > 0);
}

function engineLabel(job: SvgJob) {
  return ENGINE_OPTIONS.find((engine) => engine.value === job.engine)?.shortLabel || job.engine;
}

export function JobList({ jobs, loading = false, grouped = false, emptyMessage = "还没有绘图任务" }: { jobs: SvgJob[]; loading?: boolean; grouped?: boolean; emptyMessage?: string }) {
  if (loading) return <div className="skeleton-list" aria-label="正在加载任务">{Array.from({ length: 5 }).map((_, index) => <div className="skeleton-row" key={index} />)}</div>;
  if (jobs.length === 0) return <div className="empty-state"><FileCode2 size={28} /><strong>暂无任务</strong><span>{emptyMessage}</span></div>;

  const groups = grouped ? groupJobs(jobs) : [{ label: "", jobs }];
  return (
    <div className="job-table-wrap">
      <div className="job-table-head"><span>任务</span><span>状态与进度</span><span>引擎</span><span>创建时间</span><span /></div>
      {groups.map((group) => (
        <Fragment key={group.label || "all"}>
          {grouped && <div className="job-group-head"><strong>{group.label}</strong><span>{group.jobs.length} 条</span></div>}
          <div className="job-table-body">
            {group.jobs.map((job) => (
              <article className="job-row" key={job.id}>
                <Link className="job-title-cell" to={`/jobs/${job.id}`} title={job.source_filename}>
                  <span className="file-icon"><FileCode2 size={17} /></span>
                  <span><strong>{job.source_filename}</strong><small>{job.source_kind === "zip" ? `${job.output_count || "多"} 个输出` : "单文件任务"}{job.is_favorite && <Heart className="favorite-mark" size={13} fill="currentColor" />}</small></span>
                </Link>
                <div className="job-progress-cell">{isActive(job) ? <JobProgress job={job} compact /> : <JobStatusBadge job={job} />}</div>
                <span className="engine-chip">{engineLabel(job)}</span>
                <span className="job-time">{formatShortTime(job.created_at)}<small>{job.attempt_count > 0 ? `${job.attempt_count} 次构建` : "尚未构建"}</small></span>
                <Link className="icon-button" aria-label={`打开任务 ${job.source_filename}`} to={`/jobs/${job.id}`}><MoreHorizontal size={17} /></Link>
              </article>
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
}
